/**
 * Wealthica2 positions fetch actions (per-institution)
 *
 * Responsibilities:
 * - TTL-aware, deduplicated positions loading for a specific institution
 * - Delegates API access to `WealthicaDataService`; wires results to store
 * - Surfaces errors to store; avoids duplicate concurrent fetches
 *
 * Notes:
 * - No persistence here; store actions handle persistence separately.
 */
import { POSITIONS_TTL_MS } from '../../utils/query-config';
import { shouldFetchPositions } from '../utils/position-decisions';

export const createPositionFetchActions = (set: any, get: any) => ({
  // === POSITION LOADING ===
  loadPositionsForInstitution: async (institutionId: string) => {
    const connection = get().getConnection(institutionId);
    if (!connection) {
      throw new Error(`Connection not found for institution: ${institutionId}`);
    }

    // SHORT-TERM CACHE: Skip if positions were fetched recently and are present
    if (!shouldFetchPositions(connection)) {
      return;
    }

    // DEDUPLICATION: Prevent multiple simultaneous position fetches for same institution
    if (connection.isLoadingPositions) {
      return;
    }
    
    get().setPositionsLoadingForInstitution(institutionId, true);
    get().setPositionsErrorForInstitution(institutionId, null);

    try {
      const { WealthicaDataService } = await import('../../services/WealthicaDataService');
      
      const result = await WealthicaDataService.getPositionsByInstitution(
        connection.bearerToken,
        institutionId
      );

      if (!result.success || !result.data) {
        console.error('❌ MULTI-CONN: Position service failed', { institutionId, error: result.error });
        throw new Error(result.error || 'Failed to fetch positions from service');
      }

      const positions = result.data;
      get().setPositionsForInstitution(institutionId, positions);

    } catch (error) {
      console.error('❌ MULTI-CONN: Position fetch failed', { 
        institutionId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      get().setPositionsErrorForInstitution(institutionId, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      get().setPositionsLoadingForInstitution(institutionId, false);
    }
  },
});
